import {useState} from 'react';
import {format} from 'date-fns';
import {
  AlertTriangle,
  Download,
  ExternalLink,
  Eye,
  FileText,
  Link2,
  Loader2,
  Mail,
  MoreHorizontal,
  Upload,
  X,
} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Tooltip, TooltipContent, TooltipTrigger} from '@common-web/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@common-web/components/ui/dropdown-menu';
import {
  formatDate,
  type GroupDetail,
  type GroupPipelineDetail,
} from '@internal/pages/group-pipeline/components/pipelineDisplay';
import {EmptyTabState} from '@internal/pages/group-pipeline/components/EmptyTabState';
import {toast} from '@common-web/hooks/use-toast';
import {createMutationErrorHandler, trpc} from '@common-web/lib/trpc';
import {canViewFile} from '@common/groupFtp/files';
import {FTP_SUBFOLDER_ROLE_LABELS} from '@common/groupFtp/types';
import {FileViewerDialog} from '@internal/pages/group-pipeline/detail/ftp/FileViewer';
import {FolderChip} from '@internal/pages/group-pipeline/detail/ftp/FolderChip';
import {FtpFolderCard} from '@internal/pages/group-pipeline/detail/ftp/FtpFolderCard';
import {
  PayrollFilesSection,
  ReturnedRunsSection,
} from '@internal/pages/group-pipeline/detail/ftp/GroupFileSections';
import {LinkDocumentFileDialog} from '@internal/pages/group-pipeline/detail/ftp/LinkDocumentFileDialog';
import {UploadDocumentDialog} from '@internal/pages/group-pipeline/detail/ftp/UploadDocumentDialog';
import {useDocumentFile} from '@internal/pages/group-pipeline/detail/ftp/useDocumentFile';

type PipelineDocument = GroupPipelineDetail['documents'][number];

// Intake documents still owed. Go-live package documents aren't due until the Go Live Run.
export function missingDocumentCount(pipeline: GroupPipelineDetail | null): number {
  return (
    pipeline?.documents.filter((doc) => doc.kind !== 'GO_LIVE_PACKAGE' && doc.status !== 'RECEIVED')
      .length ?? 0
  );
}

export function GroupDocumentsTab({group, canManage}: {group: GroupDetail; canManage: boolean}) {
  // FTP files are only opened for groups in the pipeline, where each access is logged.
  if (!group.pipeline) {
    return (
      <EmptyTabState
        icon={FileText}
        title='No documents yet'
        description='Documents are tracked once the group is in the pipeline.'
      />
    );
  }
  return <PipelineFtpDocuments group={group} pipeline={group.pipeline} canManage={canManage} />;
}

function PipelineFtpDocuments({
  group,
  pipeline,
  canManage,
}: {
  group: GroupDetail;
  pipeline: GroupPipelineDetail;
  canManage: boolean;
}) {
  const folder = trpc.internal.groupFtp.folder.useQuery({vbaGroupId: group.vbaGroupId});
  const roles = new Set(folder.data?.link?.subfolders.map((s) => s.role));

  return (
    <div className='flex flex-col gap-5'>
      <FtpFolderCard vbaGroupId={group.vbaGroupId} canLink={canManage} />
      <PipelineDocuments vbaGroupId={group.vbaGroupId} pipeline={pipeline} canManage={canManage} />
      {roles.has('AUTOMATION') && (
        <PayrollFilesSection vbaGroupId={group.vbaGroupId} canManage={canManage} />
      )}
      {roles.has('RETURN') && <ReturnedRunsSection vbaGroupId={group.vbaGroupId} />}
    </div>
  );
}

function PipelineDocuments({
  vbaGroupId,
  pipeline,
  canManage,
}: {
  vbaGroupId: string;
  pipeline: GroupPipelineDetail;
  canManage: boolean;
}) {
  const utils = trpc.useUtils();
  const folder = trpc.internal.groupFtp.folder.useQuery({vbaGroupId});
  const missingFiles = trpc.internal.groupFtp.missingFiles.useQuery(
    {vbaGroupId},
    {enabled: pipeline.documents.some((doc) => doc.ftpPath)},
  );
  const missing = new Set(missingFiles.data ?? []);
  const [linking, setLinking] = useState<PipelineDocument | null>(null);
  const [uploading, setUploading] = useState<PipelineDocument | null>(null);
  const writesEnabled = folder.data?.writesEnabled ?? false;

  const link = folder.data?.link;
  // The group's subfolder for the document's destination, or why its file can't be linked yet.
  const destinationOf = (doc: PipelineDocument) => {
    if (!doc.ftpDestination) {
      return {folder: null, blocker: 'FTP save location not confirmed yet'};
    }
    const name = link?.subfolders.find((s) => s.role === doc.ftpDestination)?.name ?? null;
    const blocker = folder.isLoading
      ? 'Checking the FTP folder'
      : !link
        ? "Link the group's FTP folder first"
        : !name
          ? `Set the ${FTP_SUBFOLDER_ROLE_LABELS[doc.ftpDestination].toLowerCase()} subfolder in Manage folder mapping first`
          : null;
    return {folder: name, blocker};
  };

  const unlink = trpc.internal.groupFtp.unlinkDocumentFile.useMutation({
    onSuccess: async (_, {documentId}) => {
      await Promise.all([
        utils.internal.groupPipeline.detail.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.activity.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.list.invalidate(),
        utils.internal.groupPipeline.board.invalidate(),
        utils.internal.groupFtp.missingFiles.invalidate({vbaGroupId}),
      ]);
      const doc = pipeline.documents.find((d) => d.id === documentId);
      toast({title: `Unlinked ${doc?.fileName ?? 'the file'}`});
    },
    onError: createMutationErrorHandler("Couldn't unlink the file"),
  });

  const uploadFolder = uploading && destinationOf(uploading).folder;

  const intake = pipeline.documents.filter((doc) => doc.kind !== 'GO_LIVE_PACKAGE');
  const goLive = pipeline.documents.filter((doc) => doc.kind === 'GO_LIVE_PACKAGE');
  const rowProps = {
    vbaGroupId,
    canManage,
    destinationOf,
    writesEnabled,
    missing,
    onLink: setLinking,
    onUpload: setUploading,
    onUnlink: (doc: PipelineDocument) => unlink.mutate({vbaGroupId, documentId: doc.id}),
  };

  return (
    <section className='overflow-hidden rounded-lg border bg-white'>
      <h2 className='px-5 py-4 text-base font-semibold'>Documents</h2>
      <div className='overflow-x-auto'>
        <table className='w-full min-w-[760px] text-sm'>
          <thead className='border-y bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500'>
            <tr>
              <th className='w-32 px-5 py-2.5 font-semibold'>Status</th>
              <th className='px-3 py-2.5 font-semibold'>Document</th>
              <th className='w-40 px-3 py-2.5 font-semibold'>Saves to</th>
              <th className='px-3 py-2.5 font-semibold'>File</th>
              <th className='px-5 py-2.5' />
            </tr>
          </thead>
          <tbody>
            {intake.map((doc) => (
              <DocumentRow key={doc.id} doc={doc} {...rowProps} />
            ))}
            {goLive.length > 0 && (
              <tr className='border-b'>
                <td
                  colSpan={5}
                  className='bg-gray-50 px-5 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500'
                >
                  Go-live package · due by the Go Live Run ·{' '}
                  {goLive.filter((doc) => doc.status === 'RECEIVED').length} of {goLive.length}
                </td>
              </tr>
            )}
            {goLive.map((doc) => (
              <DocumentRow key={doc.id} doc={doc} {...rowProps} />
            ))}
          </tbody>
        </table>
      </div>
      {uploading && uploadFolder && (
        <UploadDocumentDialog
          vbaGroupId={vbaGroupId}
          doc={{...uploading, folder: uploadFolder}}
          onOpenChange={(open) => !open && setUploading(null)}
        />
      )}
      {linking?.ftpDestination && (
        <LinkDocumentFileDialog
          vbaGroupId={vbaGroupId}
          doc={{...linking, ftpDestination: linking.ftpDestination}}
          onOpenChange={(open) => !open && setLinking(null)}
        />
      )}
    </section>
  );
}

function DocumentRow({
  vbaGroupId,
  doc,
  canManage,
  destinationOf,
  writesEnabled,
  missing,
  onLink,
  onUpload,
  onUnlink,
}: {
  vbaGroupId: string;
  doc: PipelineDocument;
  canManage: boolean;
  destinationOf: (doc: PipelineDocument) => {folder: string | null; blocker: string | null};
  writesEnabled: boolean;
  missing: Set<string>;
  onLink: (doc: PipelineDocument) => void;
  onUpload: (doc: PipelineDocument) => void;
  onUnlink: (doc: PipelineDocument) => void;
}) {
  const files = useDocumentFile(vbaGroupId);
  const fileMissing = missing.has(doc.id);
  const viewable = Boolean(doc.ftpPath && canViewFile(doc.fileName ?? doc.ftpPath));
  const missingNote = "This file isn't on the FTP anymore";
  const destination = destinationOf(doc);
  const uploadBlocker =
    destination.blocker ??
    (writesEnabled ? null : "Uploads to CBS's FTP are off in this environment");

  return (
    <tr className='border-b last:border-0'>
      <td className='px-5 py-3'>
        <DocumentStatus doc={doc} />
      </td>
      <td className='px-3 py-3'>
        <p className='font-medium'>{doc.label}</p>
        {doc.status === 'REQUESTED' && <p className='text-xs text-gray-500'>Requested</p>}
        {doc.status === 'REJECTED' && doc.rejectReason && (
          <p className='text-xs text-red-700'>Rejected: {doc.rejectReason}</p>
        )}
      </td>
      <td className='px-3 py-3'>
        {destination.folder ? (
          <FolderChip name={destination.folder} />
        ) : doc.ftpDestination ? (
          <span className='text-xs text-gray-500'>
            {FTP_SUBFOLDER_ROLE_LABELS[doc.ftpDestination]} folder
          </span>
        ) : (
          <span className='text-xs text-gray-400'>Not confirmed</span>
        )}
      </td>
      <td className='px-3 py-3'>
        {doc.ftpPath ? (
          <>
            <p className='break-all font-mono text-xs'>{doc.fileName}</p>
            {fileMissing && (
              <p className='flex items-center gap-1 text-xs text-amber-800'>
                <AlertTriangle className='h-3 w-3 shrink-0' />
                Missing on the FTP
              </p>
            )}
          </>
        ) : doc.status !== 'RECEIVED' ? (
          <span className='text-gray-400'>Not received</span>
        ) : null}
      </td>
      <td className='px-5 py-3'>
        {doc.ftpPath ? (
          <div className='flex items-center justify-end gap-1'>
            {viewable && (
              <Button
                variant='ghost'
                size='sm'
                disabled={fileMissing || files.isLoading('view', doc.id)}
                title={fileMissing ? missingNote : undefined}
                onClick={() => void files.view(doc.id)}
              >
                {files.isLoading('view', doc.id) ? (
                  <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' />
                ) : (
                  <Eye className='mr-1 h-3.5 w-3.5' />
                )}
                View
              </Button>
            )}
            {files.viewing && (
              <FileViewerDialog
                file={files.viewing}
                onOpenChange={(open) => !open && files.closeViewer()}
              />
            )}
            <Button
              variant='ghost'
              size='sm'
              disabled={fileMissing || files.isLoading('download', doc.id)}
              title={fileMissing ? missingNote : undefined}
              onClick={() => void files.download(doc.id)}
            >
              {files.isLoading('download', doc.id) ? (
                <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' />
              ) : (
                <Download className='mr-1 h-3.5 w-3.5' />
              )}
              Download
            </Button>
            {(canManage || viewable) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' size='sm' aria-label={`More actions for ${doc.label}`}>
                    <MoreHorizontal className='h-4 w-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end' className='w-56'>
                  {viewable && (
                    <DropdownMenuItem disabled={fileMissing} onSelect={() => files.open(doc.id)}>
                      <ExternalLink className='mr-2 h-4 w-4' />
                      Open in new tab
                    </DropdownMenuItem>
                  )}
                  {viewable && canManage && <DropdownMenuSeparator />}
                  {canManage && (
                    <>
                      <DropdownMenuItem
                        className='flex-col items-start gap-0.5'
                        onSelect={() => onLink(doc)}
                      >
                        Change file
                        <span className='text-xs text-gray-500'>
                          Pick another file in the folder
                        </span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className='flex-col items-start gap-0.5'
                        onSelect={() => onUnlink(doc)}
                      >
                        Unlink file
                        <span className='text-xs text-gray-500'>
                          Back to not received. The file stays on the FTP.
                        </span>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ) : (
          canManage && (
            <div className='flex items-center justify-end gap-1'>
              <Button
                variant='ghost'
                size='sm'
                disabled={Boolean(uploadBlocker)}
                title={uploadBlocker ?? undefined}
                onClick={() => onUpload(doc)}
              >
                <Upload className='mr-1 h-3.5 w-3.5' />
                Upload
              </Button>
              <Button
                variant='ghost'
                size='sm'
                disabled={Boolean(destination.blocker)}
                title={destination.blocker ?? undefined}
                onClick={() => onLink(doc)}
              >
                <Link2 className='mr-1 h-3.5 w-3.5' />
                Link FTP file
              </Button>
            </div>
          )
        )}
      </td>
    </tr>
  );
}

function DocumentStatus({doc}: {doc: PipelineDocument}) {
  if (doc.status === 'RECEIVED') {
    const added = doc.fileAdded;
    const by = added?.byName ? ` by ${added.byName}` : '';
    // An FTP file's received date is a real moment, so it's shown in local time, not UTC.
    const received =
      doc.ftpPath && doc.receivedAt
        ? format(new Date(doc.receivedAt), 'MMM d, yyyy')
        : formatDate(doc.receivedAt);
    // A linked file is received when it reached the FTP, which can be well before it was linked.
    const detail = !added
      ? null
      : added.uploaded
        ? [`Uploaded ${format(new Date(added.at), 'MMM d, yyyy')}${by}`]
        : [
            `Arrived on the FTP ${received}`,
            `Linked ${format(new Date(added.at), 'MMM d, yyyy')}${by}`,
          ];
    return (
      <>
        <p className='text-sm font-semibold text-green-700'>Received</p>
        {doc.receivedAt &&
          (detail ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <p
                  tabIndex={0}
                  className='w-fit cursor-help text-xs text-gray-500 underline decoration-dotted underline-offset-2'
                >
                  {received}
                </p>
              </TooltipTrigger>
              <TooltipContent className='bg-gray-900 text-white'>
                {detail.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </TooltipContent>
            </Tooltip>
          ) : (
            <p className='text-xs text-gray-500'>{received}</p>
          ))}
      </>
    );
  }
  if (doc.status === 'REJECTED')
    return <X className='h-4 w-4 text-red-700' aria-label='Rejected' />;
  if (doc.status === 'REQUESTED')
    return <Mail className='h-4 w-4 text-gray-500' aria-label='Requested' />;
  return <span className='text-gray-400'>—</span>;
}
